import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react'
import type { MouseEvent, TouchEvent } from 'react'
import { AtlasTag } from '@diligentcorp/atlas-react'
import i18n from 'i18next'
import { css, ClassNames } from '@emotion/react'
import { DeleteDateRowIcon as IconDeleteDateRow } from 'assets/icons/DeleteDateRowIcon'
import { AtlasGreyLight, AtlasGreyMedium } from 'assets/styles/variables'
import { EllipseString } from 'assets/sharedStyles'
import { SelectChip } from './StyledComponents'
import { v4 as uuid } from 'uuid'
import styled from '@emotion/styled'
import { AtlasTooltip } from 'components/atlas-components/atlas-tooltip/Tooltip'
import { TOOLTIP_AUTO_CLOSE_DELAY } from 'common/constants/constants'

const ATTENDANCE_PARTIAL_TEXT = i18n.t('ATTENDANCE_PARTIAL')
const ATTENDANCE_FULL_TEXT = i18n.t('ATTENDANCE_FULL')
const REMOTE_TEXT = i18n.t('ATTENDANCE_REMOTE')
const IN_PERSON_TEXT = i18n.t('ATTENDANCE_IN_PERSON')

type Props = {
    className?: string
    name: string
    email: string | null
    readonly?: boolean
    onDelete?: (...args) => void
    interactiveIcon?: React.ReactNode
    secondaryInteractiveIcon?: React.ReactNode
    isTimeLate?: boolean
    isLocationRemote?: boolean
    hideIcons?: boolean
}

export const UserChip = (props: Props) => {
    const [displayType, setDisplayType] = useState<DisplayType>('name')
    const [timeTooltipOpen, setTimeTooltipOpen] = useState(false)
    const [locationTooltipOpen, setLocationTooltipOpen] = useState(false)
    const timeTooltipTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    const locationTooltipTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

    // TODO: use translate instead of t when fully committing to Atlas
    const chipEmail = !!props.email ? props.email : i18n.t('NO_EMAIL_PERSON_LABEL')
    const label = displayType === 'name' ? props.name : chipEmail

    useEffect(() => {
        if (timeTooltipOpen) {
            if (timeTooltipTimeoutRef.current) {
                clearTimeout(timeTooltipTimeoutRef.current)
            }
            timeTooltipTimeoutRef.current = setTimeout(() => {
                setTimeTooltipOpen(false)
            }, TOOLTIP_AUTO_CLOSE_DELAY)
        } else {
            if (timeTooltipTimeoutRef.current) {
                clearTimeout(timeTooltipTimeoutRef.current)
                timeTooltipTimeoutRef.current = null
            }
        }

        if (locationTooltipOpen) {
            if (locationTooltipTimeoutRef.current) {
                clearTimeout(locationTooltipTimeoutRef.current)
            }
            locationTooltipTimeoutRef.current = setTimeout(() => {
                setLocationTooltipOpen(false)
            }, TOOLTIP_AUTO_CLOSE_DELAY)
        } else {
            if (locationTooltipTimeoutRef.current) {
                clearTimeout(locationTooltipTimeoutRef.current)
                locationTooltipTimeoutRef.current = null
            }
        }

        return () => {
            if (timeTooltipTimeoutRef.current) {
                clearTimeout(timeTooltipTimeoutRef.current)
            }
            if (locationTooltipTimeoutRef.current) {
                clearTimeout(locationTooltipTimeoutRef.current)
            }
        }
    }, [timeTooltipOpen, locationTooltipOpen])

    const handleClick = useCallback((e?: MouseEvent<HTMLElement> | TouchEvent<HTMLElement>) => {
        if (e) {
            e.preventDefault()
            e.stopPropagation()
        }
        setDisplayType((prev) => (prev === 'name' ? 'email' : 'name'))
    }, [])

    const handleTouchEnd = useCallback(
        (e: TouchEvent<HTMLElement>) => {
            e.preventDefault()
            e.stopPropagation()
            handleClick(e)
        },
        [handleClick]
    )
    const key = useMemo(() => uuid(), [])

    const shouldShowTimeIcon =
        !props.hideIcons && props.interactiveIcon && props.isTimeLate !== undefined
    const shouldShowLocationIcon =
        !props.hideIcons && props.secondaryInteractiveIcon && props.isLocationRemote !== undefined
    const hasIcons = shouldShowTimeIcon || shouldShowLocationIcon

    const handleTimeIconClick = useCallback(
        (e?: MouseEvent<HTMLElement> | TouchEvent<HTMLElement>) => {
            if (e) {
                e.preventDefault()
                e.stopPropagation()
            }
            setTimeTooltipOpen(true)
        },
        []
    )

    const handleTimeIconTouchEnd = useCallback(
        (e: TouchEvent<HTMLElement>) => {
            e.preventDefault()
            e.stopPropagation()
            handleTimeIconClick(e)
        },
        [handleTimeIconClick]
    )

    const handleLocationIconClick = useCallback(
        (e?: MouseEvent<HTMLElement> | TouchEvent<HTMLElement>) => {
            if (e) {
                e.preventDefault()
                e.stopPropagation()
            }
            setLocationTooltipOpen(true)
        },
        []
    )

    const handleLocationIconTouchEnd = useCallback(
        (e: TouchEvent<HTMLElement>) => {
            e.preventDefault()
            e.stopPropagation()
            handleLocationIconClick(e)
        },
        [handleLocationIconClick]
    )

    const renderIcons = () => {
        if (!hasIcons) return null
        return (
            <>
                {shouldShowTimeIcon && (
                    <AtlasTooltip
                        title={props.isTimeLate ? ATTENDANCE_PARTIAL_TEXT : ATTENDANCE_FULL_TEXT}
                        placement="top"
                        open={timeTooltipOpen}
                        onOpen={() => setTimeTooltipOpen(true)}
                        onClose={() => setTimeTooltipOpen(false)}
                        disableHoverListener={false}
                        disableTouchListener>
                        <IconWrapper
                            onClick={handleTimeIconClick}
                            onTouchEnd={handleTimeIconTouchEnd}>
                            {props.interactiveIcon}
                        </IconWrapper>
                    </AtlasTooltip>
                )}
                {shouldShowLocationIcon && (
                    <AtlasTooltip
                        title={props.isLocationRemote ? REMOTE_TEXT : IN_PERSON_TEXT}
                        placement="top"
                        open={locationTooltipOpen}
                        onOpen={() => setLocationTooltipOpen(true)}
                        onClose={() => setLocationTooltipOpen(false)}
                        disableHoverListener={false}
                        disableTouchListener>
                        <IconWrapper
                            onClick={handleLocationIconClick}
                            onTouchEnd={handleLocationIconTouchEnd}>
                            {props.secondaryInteractiveIcon}
                        </IconWrapper>
                    </AtlasTooltip>
                )}
            </>
        )
    }

    return props.readonly ? (
        <ClassNames>
            {({ css }) => (
                <AtlasTag
                    className={css`
                        ${props.className} ${ChipClass}
                    `}
                    color={AtlasGreyLight}
                    data-testid={`Chip_${key}`}
                    tabIndex={-1}
                    onClick={handleClick}
                    onTouchEnd={handleTouchEnd}>
                    {hasIcons ? (
                        <>
                            <EllipseString>{label}</EllipseString>
                            {renderIcons()}
                        </>
                    ) : (
                        <EllipseString>{label}</EllipseString>
                    )}
                </AtlasTag>
            )}
        </ClassNames>
    ) : (
        // Atlas Chip

        // <AtlasChip
        //     className={ChipClass}
        //     data-testid={`Chip_${key}`}
        //     tabIndex={-1}
        //     onClick={handleClick}>
        //     <Inline>
        //         <EllipseString>{label}</EllipseString>
        //         <StyledAtlasIcon name="close" size="16" />
        //     </Inline>
        // </AtlasChip>

        // Use until we commit to Atlas
        <SelectChip
            className={props.className}
            key={key}
            data-testid={`Chip_${key}`}
            tabIndex={-1}
            label={
                hasIcons ? (
                    <LabelContainer>
                        {label}
                        {renderIcons()}
                    </LabelContainer>
                ) : (
                    label
                )
            }
            onClick={handleClick}
            onTouchEnd={handleTouchEnd}
            deleteIcon={<StyledIconDelete onTouchEnd={props.onDelete} />}
            onDelete={props.onDelete}
        />
    )
}

const ChipClass = css`
    display: inline-block;
    max-width: 335px;
    margin: 10px 20px 5px 0px;
    outline: none;
    cursor: pointer;
    touch-action: manipulation;
`
const StyledIconDelete = styled(IconDeleteDateRow)`
    display: inline-block;
    fill: ${AtlasGreyMedium};
    stroke: inherit;
    width: 20px;
    height: 20px;
`

const LabelContainer = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    touch-action: manipulation;
`

const IconWrapper = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: 4px;
    flex-shrink: 0;
`

// const StyledAtlasIcon = styled(AtlasIcon)`
//     padding-left: 5px;
// `
// const Inline = styled('div')`
//     display: flex;
// `

type DisplayType = 'name' | 'email'

export default UserChip
